import { type AppThunk, type AppDispatch } from "../../../../Application/globaltypes/redux";
import {
  setError as setOverviewError,
  setFilterOptions as setOverviewFilterOptions,
  setIsLoading as setOverviewIsLoading,
} from "../ExternalLinksFiltersSlice";
import {
  getLink,
  setLink,
  getLinkFailure,
  setTags,
  setGroups,
  setGroupsFailure,
  setUsers,
  setUsersFailure,
  fetchUsersStart,
  fetchGroupsStart,
  setTagsError,
} from "../ExternalLinkDetailsSlice";
import { openEntityDetails, setEntityId } from "../ExternalLinkEntityStateSlice";
import { type ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { mapFilterOption, type FiltersMap, type GenericFiltersMap } from "utils/filterUtils";
import { ExternalLinksFiltersEnum, externalLinksFilterTypes } from "../../types/models";
import PromiseStatuses from "enums/promiseStatuses";
import { pick } from "lodash";
import { TagsEnumCamelPlural } from "interfaces/TagsEnum";
import { type AccountBase, type PackName } from "interfaces";
import objUtils from "../../../../../utils/objectUtils";
import DataService from "features/Application/services/dataServices/typedDataService";
import { fetchBegin, fetchSuccess } from "../ExternalLinksOverviewSlice";
import { escapeTerm } from "utils/searchUtils";
import { formatFilters } from "utils/filterMapUtils";
import { SortingDirection } from "enums";
import { type Filters } from "utils/queryUtils";
import * as contentAssignments from "../../../../People/ContentAssignments/state/slices/contentAssignmentsFiltersSlice";

const defaultFiltersOptions = [
  ExternalLinksFiltersEnum.Publishers,
  ExternalLinksFiltersEnum.Tags,
  ExternalLinksFiltersEnum.Packs,
];

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const fetchExternalLinks =
  (skip: number, top: number, sortBy?: string, sortOrder?: string, append?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBegin());
      const filtersState = getState().library.externalLinks.filters;
      const searchTerm = escapeTerm(filtersState.search);
      const filterQueryParams = formatFilters(filtersState.appliedFilter, externalLinksFilterTypes);
      const result = await DataService.content.getContent({
        top,
        skip,
        sortBy,
        sortOrder,
        filters: filterQueryParams,
        term: searchTerm,
        showPurchased: true,
        contentType: "link",
      });

      const items = result.data.map((item) => ({ ...item, thumbnailUrl: item.bag?.thumbnailUrl }));
      const totalCount = Number.parseInt(result.headers[countHeaderName]);
      dispatch(
        fetchSuccess({
          items,
          totalCount,
          append,
        }),
      );
    } catch {
      dispatch(
        fetchSuccess({
          items: [],
          totalCount: 0,
        }),
      );
    }
  };

export const getExternalLink = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(getLink());
  try {
    const response = await DataService.links.getExternalLinkById(id);
    dispatch(setLink(response));
    dispatch(openEntityDetails({ id, isDraft: response.isDraft, dateModified: response.dateModified }));
    dispatch(setEntityId(id));
  } catch (error) {
    dispatch(getLinkFailure((error as Error).message));
  }
};

/**
 * Fetches all available tags for external links
 */
export const fetchExternalLinkTags = () => async (dispatch: AppDispatch) => {
  try {
    const tags = await DataService.links.getExternalLinkTags();
    dispatch(setTags(tags));
  } catch (error) {
    console.error("Failed to fetch external link tags:", error);
    dispatch(setTagsError(error));
  }
};

export const fetchExternalLinkGroups = (linkId: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(fetchGroupsStart());
    const groups = await DataService.links.getExternalLinkGroups(linkId, 0, 10, "name", SortingDirection.Ascending, {});

    groups.data.forEach((item: any) => (item.id = item.groupId));

    dispatch(setGroups({ items: groups.data, count: Number.parseInt(groups.headers[countHeaderName]) }));
  } catch (error) {
    dispatch(setGroupsFailure((error as Error).message));
  }
};

export const fetchExternalLinkUsers =
  (
    linkId: number,
    skip: number,
    top: number,
    sortBy: string,
    sortOrder: SortingDirection,
    filters: Filters,
    term?: string,
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(fetchUsersStart());
      const users = await DataService.links.getExternalLinkUsers(linkId, skip, top, sortBy, sortOrder, filters, term);
      dispatch(setUsers({ items: users.data, count: Number.parseInt(users.headers[countHeaderName]) }));
    } catch (error) {
      dispatch(setUsersFailure((error as Error).message));
    }
  };

export const getOverviewFilterOptions = () =>
  getFilterOptionsWrapper(setOverviewIsLoading, setOverviewFilterOptions, setOverviewError);

export const getContentAssignmentModalFilterOptions = () =>
  getFilterOptionsWrapper(
    contentAssignments.setIsLoading,
    contentAssignments.setFilterOptions,
    contentAssignments.setError,
  );

const getFilterOptionsWrapper = (
  setIsLoading: ActionCreatorWithPayload<boolean>,
  setFilterOptions: ActionCreatorWithPayload<GenericFiltersMap<string>>,
  setError: ActionCreatorWithPayload<Error>,
) => {
  return (dispatch: AppDispatch) =>
    getFilterOptions({
      showPurchased: true,
      filters: [ExternalLinksFiltersEnum.Publishers, ExternalLinksFiltersEnum.Tags, ExternalLinksFiltersEnum.Packs],
      begin: () => dispatch(setIsLoading(true)),
      success: (filterOptions: FiltersMap) => {
        dispatch(setFilterOptions(filterOptions));
        dispatch(setIsLoading(false));
      },
      failure: (error: Error) => {
        dispatch(setError(error));
        dispatch(setIsLoading(false));
      },
    });
};

export interface GetFilterOptionsProps {
  showPurchased?: boolean;
  filters?: ExternalLinksFiltersEnum[];
}

interface BaseFilterOptionsProps {
  begin: () => void;
  success: (filterOptions: FiltersMap) => void;
  failure: (error: Error) => void;
}

export const getFilterOptions = async (
  props: BaseFilterOptionsProps & GetFilterOptionsProps,
  includePublishers: boolean = true,
) => {
  const { showPurchased, begin, success, failure } = props;
  const selectedFilters = !props.filters?.length ? defaultFiltersOptions : props.filters;

  let filterOptions: FiltersMap = {};

  const tagToFilterMap: { [key in TagsEnumCamelPlural]?: ExternalLinksFiltersEnum } = {
    [TagsEnumCamelPlural.Label]: ExternalLinksFiltersEnum.Labels,
    [TagsEnumCamelPlural.SoftwareApplication]: ExternalLinksFiltersEnum.SoftwareApplications,
  };

  const readPublishersData = async () => {
    const publishers = await DataService.links.getPublishersFilterOptions();
    filterOptions = mapFilterOption(
      filterOptions,
      publishers.data,
      ExternalLinksFiltersEnum.Publishers,
      (item: PackName) => ({
        text: item.name,
        value: item.id,
      }),
    );
  };

  const readPacksData = async () => {
    const packs = await DataService.packs.getPacksFilterOptions("link", !!showPurchased);
    filterOptions = mapFilterOption(filterOptions, packs.data, ExternalLinksFiltersEnum.Packs, (item: AccountBase) => ({
      text: item.name,
      value: item.id,
    }));
  };

  const readTagsData = async () => {
    const tags = await DataService.links.getTagsFilterOptions(!!showPurchased);
    for (const [key, value] of objUtils.typedEntries<TagsEnumCamelPlural, string[]>(tags.data)) {
      if (tagToFilterMap[key]) {
        filterOptions = mapFilterOption(filterOptions, value, tagToFilterMap[key], (item: string) => ({
          text: item,
          value: item,
        }));
      }
    }
  };

  const filterHandlers: { [key in ExternalLinksFiltersEnum]?: () => Promise<void> } = {
    [ExternalLinksFiltersEnum.Packs]: readPacksData,
    [ExternalLinksFiltersEnum.Tags]: readTagsData,
    ...(includePublishers && { [ExternalLinksFiltersEnum.Publishers]: readPublishersData }),
  };

  begin();

  const filtersHandlersPromises = Object.values(pick(filterHandlers, selectedFilters)).map(
    (handler: () => Promise<void>) => handler(),
  );

  const result = await Promise.allSettled(filtersHandlersPromises);

  let someFulfilled = false;
  result.forEach((item) => {
    ({
      [PromiseStatuses.rejected]: () => failure((item as PromiseRejectedResult).reason),
      [PromiseStatuses.fulfilled]: () => (someFulfilled = true),
    })[item.status]();
  });

  if (someFulfilled) {
    success(filterOptions);
  }
};
